import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom, timeout } from 'rxjs';

import { AppConfigService } from '~/core/services/appconfig.service';
import { FoundDocument, MissingDocument, PublishedPack, documentLandscape } from '~/models/document-finder.models';

const DF = 'api/document-finder';
const enc = encodeURIComponent;

/**
 * Every backend endpoint, relative to `apiUrl` in `assets/config.json`.
 * Request/response samples: webapp/frontend/API_ENDPOINTS.md.
 */
export const DF_ENDPOINTS = {
    health: 'health',                                                   // GET -> { status }
    ready: 'ready',                                                     // GET -> { status }
    config: 'api/config',                                               // GET -> ClientConfig
    capabilities: `${DF}/capabilities`,                                 // GET -> Capabilities
    bootstrap: `${DF}/bootstrap`,                                       // GET -> Bootstrap
    l2Processes: `${DF}/l2-processes`,                                  // GET -> L2Process[]
    sources: `${DF}/sources`,                                           // GET -> ApiSource[]
    processMap: `${DF}/process-map`,                                    // GET -> ProcessMapEntry[]
    taxonomy: `${DF}/taxonomy`,                                         // GET -> Taxonomy
    search: `${DF}/search`,                                             // POST SearchApiBody -> SearchEnvelope
    signavioSearch: 'api/signavio/search',
    signavioCount: 'api/signavio/count',
    signavioDownload: (id: string) => `api/signavio/flows/${enc(id)}/download`,
    searchRuns: `${DF}/search-runs`,                                    // POST SearchApiBody -> Run | GET -> Run[]
    searchRun: (runId: string) => `${DF}/search-runs/${enc(runId)}`,    // GET -> Run
    review: (runId: string) => `${DF}/search-runs/${enc(runId)}/review`, // PUT Review -> Review
    missingUpload: (runId: string) => `${DF}/search-runs/${enc(runId)}/missing-documents/upload`,
    missingEdit: (runId: string, id: string) => `${DF}/search-runs/${enc(runId)}/missing-documents/${enc(id)}`,
    missingContent: (id: string) => `${DF}/uploads/${enc(id)}/content`,
    trace: (runId: string) => `${DF}/search/${enc(runId)}/trace`,       // GET -> RunTrace
    decision: (id: string) => `${DF}/documents/${enc(id)}/decision`,    // PUT DecisionBody -> DecisionResult
    preview: (id: string) => `${DF}/documents/${enc(id)}/preview`,      // GET -> { text }
    content: (id: string) => `${DF}/documents/${enc(id)}/content`,      // GET -> file (blob)
    link: (id: string) => `${DF}/documents/${enc(id)}/link`,            // GET -> { url }
    publish: `${DF}/packs`,                                             // POST PublishRequest -> PublishedPack
    packs: `${DF}/packs`,                                               // GET -> Pack[]
    pack: (packId: string) => `${DF}/packs/${enc(packId)}`,             // GET -> Pack
    packDownload: (packId: string) => `${DF}/packs/${enc(packId)}/download`, // GET -> zip (blob)
    deck: `${DF}/deck`,                                                 // POST DeckBody -> pptx (blob)
    feedback: `${DF}/feedback`,                                         // POST FeedbackBody -> { id, at }
    history: `${DF}/history`,                                           // GET -> HistoryRow[] | POST HistoryBody -> HistoryRow
    inventory: `${DF}/inventory`,                                       // POST InventoryBody -> InventoryPage
    inventoryExport: `${DF}/inventory/export`,                          // POST InventoryScope -> csv (blob)
    pblDuplicateCheck: `${DF}/pbl/duplicate-check`,                     // POST { items } -> PblCheckResponse
    pblGenerate: `${DF}/pbl/generate`,                                  // POST { runId, documentIds } -> PblGenerateJob
    pblGenerateJob: (jobId: string) => `${DF}/pbl/generate/${enc(jobId)}` // GET -> PblGenerateJob
};

//******************************************************************************
//  API types (see API_ENDPOINTS.md)
//******************************************************************************
export type ApiDecision = 'inc' | 'exc' | 'pend' | 'call';

export interface ClientConfig {
    apiUrl: string; authMode: string; tenantId: string; clientId: string;
    authority: string; scope: string; redirectUri: string; searchTimeoutSeconds: number;
}
export interface Capabilities { explicit_inventory: boolean; model_configured: boolean; model_verified: boolean; }
export interface ApiL2Process { id: string; name: string; }
export interface ApiSource { key: string; name: string; n: number | null; }
export interface Bootstrap {
    user: string;
    default_query?: string;
    signals?: [string, string][];
    suggestions?: string[];
    l2: ApiL2Process[];
    sources: ApiSource[];
    types: Record<string, string>;
    reasons: string[];
    landscapes: string[];
    process_map: { level: string; code: string; name: string; count: number }[];
    taxonomy: { value: string; type: string; count: number }[];
}
export interface ProcessMapEntry { code: string; name: string; description: string; }
export interface Taxonomy {
    rows: { document: string; asis: string; tobe: string; role: string }[];
    acronyms: { term: string; meaning: string }[];
}
export interface SearchSummary {
    filesScanned: number; candidates: number; found: number;
    relevant: number; needsCall: number; excluded: number;
}
export interface SearchStep { key: string; title: string; detail: string; durationSec: number; status: 'done'; }
export interface ToBeL2Context { code: string; process_name: string; }
export interface RetrievalContext {
    l1_code?: string | null;
    l1_process_name?: string | null;
    to_be_l2: ToBeL2Context[];
    key_terms: string[];
    requested_document_types?: string[] | null;
}
export interface SearchEnvelope {
    runId: string;
    summary: SearchSummary;
    steps: SearchStep[];
    documents: FoundDocument[];
    missing: MissingDocument[];
    retrieval_context?: RetrievalContext | null;
}
export interface Review {
    version: number;
    decisions: Record<string, 'inc' | 'exc' | 'pend'>;
    reasons: Record<string, string>;
    notes: Record<string, string>;
    missing: MissingDocument[];
    not_applicable: string[];
}
export interface Run {
    id: string; query: string; scope: SearchApiBody; at: string;
    status: 'running' | 'complete' | 'failed';
    documents: any[]; warnings: string[]; timings_ms: Record<string, number>;
    total: number | null; error: string | null; review: Review;
    retrieval_context?: RetrievalContext | null;
}
export interface RunTrace {
    runId: string; status: string;
    steps: { title: string; detail: string; log: string[]; ms: number | null }[];
}
export interface DecisionBody { decision: ApiDecision; reason?: string; note?: string; }
export interface DecisionResult { id: string; decision: ApiDecision; version: number; }
export interface Pack {
    id: string; runId: string; name: string; note: string; at: string; n: number;
    documents: any[]; review: Review;
}
export interface DeckBody { packId: string; name?: string; prompt?: string; }
export interface FeedbackBody {
    packId?: string | null;
    decisions?: Record<string, ApiDecision>;
    reasons?: Record<string, string>;
    comments?: string;
}
export interface HistoryRow { id: string; query: string; date: string; n: number; status: string; scope?: any; }
export interface HistoryBody { query: string; scope?: any; n?: number; status?: string; }
export interface InventoryScope {
    scenario?: string | null; process_codes?: string[];
    lifecycle?: 'all' | 'asis' | 'tobe' | 'tagged' | 'unknown';
    families?: string[]; types?: string[]; subtypes?: string[]; sources?: string[];
    include_duplicates?: boolean;
}
export interface InventoryBody { scope?: InventoryScope; page_size?: number; cursor?: string | null; }
export interface InventoryPage {
    documents: any[]; matched_total: number; returned_count: number; next_cursor: string | null;
    scope: InventoryScope; lifecycle_counts: Record<string, number>; warnings: string[];
    complete: boolean; captured_at_epoch: number; total_pages_consumed_rows: number;
}

export interface SearchRequest {
    query: string;
    l2: string[];
    land: string;
    types: string[];
    sources: string[];
}

/** Body sent to POST api/document-finder/search-runs. */
export interface SearchApiBody extends SearchRequest {
    top_k: number;
    include_duplicates: boolean;
    sort: string;
}

const SEARCH_TOP_K = 30;
const SEARCH_SORT = 'Score';

export interface SignavioFlow {
    id: string;
    process_name?: string;
    diagram_name?: string;
    file_name?: string;
    path_text?: string;
    source_blob?: string;
    candidate_codes?: string[];
    summary?: string;
    version?: string;
    relevance_reason?: string;
    relevance_confidence?: number;
}

export function consolidateDocuments(documents: FoundDocument[], flows: SignavioFlow[], topK: number): FoundDocument[] {
    const unique = flows.filter((flow, index) => flows.findIndex(other => other.id === flow.id) === index);
    const signavio = unique.slice(0, topK).map(flow => normalizeDocument({
        id: `signavio:${flow.id}`, signavioFlowId: flow.id, t: 'signavio', land: 'tobe', ext: 'svg',
        name: flow.process_name || flow.diagram_name || flow.file_name || flow.id,
        src: 'sg', path: flow.path_text || flow.source_blob || '', l2: [], codes: flow.candidate_codes ?? [],
        conf: flow.relevance_confidence ?? 0, why: flow.relevance_reason || flow.summary || '',
        summary: flow.summary, version: flow.version, mod: '', by: '', sig: [], agent: 'inc', readable: true
    }));
    return [...signavio, ...documents.slice(0, SEARCH_TOP_K - signavio.length)];
}

/** Maps a raw API document to the app model (fills missing arrays, turns null into undefined). */
function normalizeDocument(d: any): FoundDocument {
    return {
        ...d,
        land: documentLandscape(d),
        l2: d.l2 ?? [],
        codes: d.codes ?? [],
        sig: d.sig ?? [],
        copies: d.copies ?? [],
        alternates: d.alternates ?? [],
        flag: d.flag ?? undefined,
        version: d.version ?? undefined
    } as FoundDocument;
}

export interface PublishRequest {
    name: string;
    note: string;
    documentIds: string[];
    excluded: { id: string; reason: string; why?: string }[];
    missing: MissingDocument[];
}

export type PblDuplicateStatus = 'likely_duplicate' | 'possible_duplicate' | 'net_new';
export interface PblCheckItem { id: string; title: string; text?: string; }
export interface PblMatch {
    pblId: string;
    title: string;
    info: Record<string, string>;
    score: number;
    label: string;
    reason: string;
    ai: boolean;
}
export interface PblCheckResult { id: string; status: PblDuplicateStatus; matches: PblMatch[]; }
export interface PblCheckResponse {
    results: PblCheckResult[];
    counts: { likely: number; possible: number; netNew: number };
    method: 'ai' | 'rules';
    semantic: boolean;
    warnings: string[];
    threshold: number;
    source: { label: string; file: string; modifiedAt: string | null; rows: number; total: number; filter: string };
}

/** A PBL drafted by the generator from the approved documents (same shape as a PBLs-step card). */
export interface GeneratedPbl {
    id: string;
    kind: 'FIT' | 'GAP';
    priority: 'High' | 'Medium' | 'Low';
    tag: string;
    title: string;
    story: string;
    context: string;
    chips: string[];
    status: 'pending';
    reasoning: string;
    l3?: string;
    subTeam?: 'AM' | 'MM' | 'FIN' | 'HCVC';
    extension?: string;
    acceptance: string[];
    value: string;
    dependencies: string[];
    assumptions: string[];
    openQuestions: string[];
    sources: string[];
    match?: { pblId: string; overlap: number };
    /** Definition of Done points the reviewer should look at. */
    checks: string[];
}
export interface PblGenerateJob {
    jobId: string;
    runId: string;
    status: 'running' | 'done' | 'failed';
    stage: string;
    progress: number;
    message: string;
    items: GeneratedPbl[];
    warnings: string[];
    summary: { documents: number; parts: number; requirements: number; pbls: number; matched: number; netNew: number } | null;
    duplicateCheck: PblCheckResponse | null;
}

const TIMEOUT = 60_000;
const PBL_CHECK_TIMEOUT = 90_000;
const SEARCH_TIMEOUT = 190_000; // backend search timeout is 180 s
const DECK_TIMEOUT = 300_000; // allow the backend's 240 s generation timeout plus asset loading

/**
 * Single place where Document Finder talks to the backend.
 * While `apiUrl` is empty (no APIs yet) every call returns the built-in mock data, so the UI works as before.
 */
@Injectable({ providedIn: 'root' })
export class DocumentFinderApiService {
    private http = inject(HttpClient, { optional: true });
    private config = inject(AppConfigService, { optional: true });

    /** True when an API base URL is configured; otherwise mock data is used. */
    public get enabled(): boolean {
        return !!this.http && !!this.baseUrl;
    }

    private get baseUrl(): string {
        const url: string = this.config?.getConfig()?.apiUrl ?? '';
        return url.replace(/\/+$/, '');
    }

    private url(path: string): string {
        return `${this.baseUrl}/${path}`;
    }

    /** Run id of the last search (`runId` / X-Run-Id), or null. */
    public lastRunId: string | null = null;
    public uploadMissing(document: MissingDocument, file: File, uploadId: string): Promise<MissingDocument> {
        if (!this.enabled || !this.lastRunId) {
            return Promise.reject(new Error('Run a backend search before uploading a document.'));
        }
        const body = new FormData();
        body.append('upload_id', uploadId);
        body.append('document', JSON.stringify(document));
        body.append('file', file, file.name);
        return this.post<MissingDocument>(
            DF_ENDPOINTS.missingUpload(this.lastRunId), body, 180_000,
        );
    }

    public downloadMissing(uploadId: string): Promise<Blob> {
        if (!this.enabled) { return Promise.reject(new Error('The backend is not configured.')); }
        return this.getBlob(DF_ENDPOINTS.missingContent(uploadId));
    }

    public editUploadedMissing(document: MissingDocument): Promise<MissingDocument> {
        if (!this.enabled || !this.lastRunId || !document.id) {
            return Promise.reject(new Error('Reopen the original search before editing its attachment.'));
        }
        return this.put<MissingDocument>(
            DF_ENDPOINTS.missingEdit(this.lastRunId, document.id), document,
        );
    }
    /** Full envelope of the last search (summary, steps, missing), or null. */
    public lastSearch: SearchEnvelope | null = null;
    public signavioError = '';

    private get<T>(path: string, ms: number = TIMEOUT): Promise<T> {
        return firstValueFrom(this.http!.get<T>(this.url(path)).pipe(timeout(ms)));
    }

    private post<T>(path: string, body: unknown, ms: number = TIMEOUT): Promise<T> {
        return firstValueFrom(this.http!.post<T>(this.url(path), body).pipe(timeout(ms)));
    }

    private put<T>(path: string, body: unknown): Promise<T> {
        return firstValueFrom(this.http!.put<T>(this.url(path), body).pipe(timeout(TIMEOUT)));
    }

    private getBlob(path: string): Promise<Blob> {
        return firstValueFrom(this.http!.get(this.url(path), { responseType: 'blob' }).pipe(timeout(TIMEOUT)));
    }

    private postBlob(path: string, body: unknown, ms: number = TIMEOUT): Promise<Blob> {
        return firstValueFrom(this.http!.post(this.url(path), body, { responseType: 'blob' }).pipe(timeout(ms)));
    }

    //*************************************************************************
    //  Service / reference data
    //*************************************************************************
    public health(): Promise<{ status: string }> { return this.get(DF_ENDPOINTS.health); }
    public ready(): Promise<{ status: string }> { return this.get(DF_ENDPOINTS.ready); }
    public clientConfig(): Promise<ClientConfig> { return this.get(DF_ENDPOINTS.config); }
    public capabilities(): Promise<Capabilities> { return this.get(DF_ENDPOINTS.capabilities); }
    public signavioCount(): Promise<{ total_documents: number }> { return this.get(DF_ENDPOINTS.signavioCount); }

    /** All reference data in one call, or null when no API is configured. */
    public async bootstrap(): Promise<Bootstrap | null> {
        if (!this.enabled) { return null; }
        return this.get<Bootstrap>(DF_ENDPOINTS.bootstrap);
    }

    public async getL2Processes(): Promise<ApiL2Process[] | null> {
        if (!this.enabled) { return null; }
        return this.get<ApiL2Process[]>(DF_ENDPOINTS.l2Processes);
    }

    public async getSources(): Promise<ApiSource[] | null> {
        if (!this.enabled) { return null; }
        return this.get<ApiSource[]>(DF_ENDPOINTS.sources);
    }

    public async getProcessMap(): Promise<ProcessMapEntry[] | null> {
        if (!this.enabled) { return null; }
        return this.get<ProcessMapEntry[]>(DF_ENDPOINTS.processMap);
    }

    public async getTaxonomy(): Promise<Taxonomy | null> {
        if (!this.enabled) { return null; }
        return this.get<Taxonomy>(DF_ENDPOINTS.taxonomy);
    }

    //*************************************************************************
    //  Search
    //*************************************************************************
    private searchBody(req: SearchRequest): SearchApiBody {
        return {
            query: req.query,
            l2: req.l2,
            land: req.land,
            types: req.types.some(type => type !== 'signavio') ? req.types.filter(type => type !== 'signavio') : ['flow'],
            sources: req.sources.some(source => source !== 'sg') ? req.sources.filter(source => source !== 'sg') : ['az'],
            top_k: SEARCH_TOP_K,
            include_duplicates: false,
            sort: SEARCH_SORT
        };
    }

    /** Runs a search (POST /search) and returns its documents. */
    public async search(req: SearchRequest): Promise<FoundDocument[]> {
        if (!this.enabled) { throw new Error('API is not configured (assets/config.json → apiUrl).'); }
        this.signavioError = '';
        const configured = Number(this.config?.getConfig()?.signavioTopK ?? 5);
        const topK = Number.isFinite(configured) ? Math.max(1, Math.min(15, Math.floor(configured))) : 5;
        const res = await this.post<SearchEnvelope>(DF_ENDPOINTS.search, this.searchBody(req), SEARCH_TIMEOUT);
        const context = res.retrieval_context;
        const requestedTypes = context?.requested_document_types;
        const eligible = req.land !== 'asis' && req.types.includes('signavio') && req.sources.includes('sg')
            && (!requestedTypes?.length || requestedTypes.includes('Signavio'));
        let signavio: { flows: SignavioFlow[]; total_documents?: number } = { flows: [], total_documents: 0 };
        if (eligible) {
            const body: Record<string, unknown> = { query: req.query, user_query: req.query, top_k: topK };
            if (context?.l1_code) { body.l1_code = context.l1_code; }
            if (context?.l1_process_name) { body.l1_process_name = context.l1_process_name; }
            if (context?.to_be_l2?.length) { body.to_be_l2 = context.to_be_l2; }
            if (context?.key_terms?.length) { body.key_terms = context.key_terms; }
            signavio = await this.post<{ flows: SignavioFlow[]; total_documents?: number }>(
                DF_ENDPOINTS.signavioSearch, body, SEARCH_TIMEOUT
            ).catch(() => {
                this.signavioError = 'Signavio search is unavailable. Showing other document results.';
                return { flows: [] as SignavioFlow[], total_documents: 0 };
            });
        }
        const ragEligible = req.types.some(type => type !== 'signavio') && req.sources.some(source => source !== 'sg')
            && (!requestedTypes?.length || requestedTypes.some(type => type !== 'Signavio'));
        const documents = consolidateDocuments(ragEligible ? (res.documents ?? []).map(normalizeDocument) : [], eligible ? (signavio.flows ?? []) : [], topK);
        this.lastSearch = {
            ...res, documents, missing: res.missing ?? [],
            summary: { ...res.summary,
                filesScanned: (res.summary?.filesScanned ?? 0) + (eligible ? (signavio.total_documents ?? 0) : 0),
                found: documents.length,
                relevant: documents.filter(doc => doc.agent === 'inc').length,
                needsCall: documents.filter(doc => doc.agent === 'call').length,
                excluded: documents.filter(doc => doc.agent === 'exc').length }
        };
        this.lastRunId = res.runId ?? null;
        return documents;
    }

    public downloadSignavio(id: string): Promise<Blob> {
        return firstValueFrom(this.http!.post(this.url(DF_ENDPOINTS.signavioDownload(id)), {}, {
            responseType: 'blob'
        }).pipe(timeout(310_000)));
    }

    public getRuns(): Promise<Run[]> { return this.get(DF_ENDPOINTS.searchRuns); }
    public getRun(runId: string): Promise<Run> { return this.get(DF_ENDPOINTS.searchRun(runId)); }
    public getTrace(runId: string): Promise<RunTrace> { return this.get(DF_ENDPOINTS.trace(runId)); }

    /** Replaces the whole review; `review.version` must match the stored version (409 otherwise). */
    public saveReview(runId: string, review: Review): Promise<Review> {
        return this.put(DF_ENDPOINTS.review(runId), review);
    }

    //*************************************************************************
    //  Review / documents
    //*************************************************************************
    /** Saves one decision on the server; resolves null when no API is configured. */
    public async setDecision(id: string, body: DecisionBody): Promise<DecisionResult | null> {
        if (!this.enabled) { return null; }
        return this.put<DecisionResult>(DF_ENDPOINTS.decision(id), body);
    }

    public async getPreview(id: string): Promise<string | null> {
        if (!this.enabled) { return null; }
        return (await this.get<{ text: string }>(DF_ENDPOINTS.preview(id))).text;
    }

    /** Binary content of a document, or null when no API is configured. */
    public async getContent(id: string): Promise<Blob | null> {
        if (!this.enabled) { return null; }
        return this.getBlob(DF_ENDPOINTS.content(id));
    }

    public async getLink(id: string): Promise<string | null> {
        if (!this.enabled) { return null; }
        return (await this.get<{ url: string }>(DF_ENDPOINTS.link(id))).url;
    }

    public async sendFeedback(body: FeedbackBody): Promise<{ id: string; at: string } | null> {
        if (!this.enabled) { return null; }
        return this.post(DF_ENDPOINTS.feedback, body);
    }

    //*************************************************************************
    //  Packs
    //*************************************************************************
    /** Saves the pack on the server, or returns null when no API is configured (the local id is kept). */
    public async publishPack(req: PublishRequest): Promise<PublishedPack | null> {
        if (!this.enabled) { return null; }
        return this.post<PublishedPack>(DF_ENDPOINTS.publish, req);
    }

    public async getPacks(): Promise<Pack[] | null> {
        if (!this.enabled) { return null; }
        return this.get<Pack[]>(DF_ENDPOINTS.packs);
    }

    public async getPack(packId: string): Promise<Pack | null> {
        if (!this.enabled) { return null; }
        return this.get<Pack>(DF_ENDPOINTS.pack(packId));
    }

    /** Server-built ZIP (files + manifest.csv + README.txt), or null when no API is configured. */
    public async downloadPack(packId: string): Promise<Blob | null> {
        if (!this.enabled) { return null; }
        return this.getBlob(DF_ENDPOINTS.packDownload(packId));
    }

    /** Server-built PowerPoint, or null when no API is configured. */
    public async buildDeck(body: DeckBody): Promise<Blob | null> {
        if (!this.enabled) { return null; }
        try {
            return await this.postBlob(DF_ENDPOINTS.deck, body, DECK_TIMEOUT);
        } catch (error) {
            if (error instanceof HttpErrorResponse && error.error instanceof Blob
                && error.error.type.includes('json')) {
                let payload: unknown;
                try {
                    payload = JSON.parse(await error.error.text());
                } catch (parseError) {
                    console.error('Could not read the deck API error response', parseError);
                    throw error;
                }
                if (payload && typeof payload === 'object' && 'error' in payload
                    && payload.error && typeof payload.error === 'object'
                    && 'message' in payload.error && typeof payload.error.message === 'string') {
                    const requestId = 'requestId' in payload.error && typeof payload.error.requestId === 'string'
                        ? ` (Request ID: ${payload.error.requestId})` : '';
                    throw new Error(payload.error.message + requestId);
                }
            }
            if (error instanceof HttpErrorResponse && error.status === 0) {
                throw new Error('Cannot reach the deck service. Check the backend connection and allowed origins.');
            }
            throw error;
        }
    }

    //*************************************************************************
    //  History / inventory
    //*************************************************************************
    public async getHistory(): Promise<HistoryRow[] | null> {
        if (!this.enabled) { return null; }
        return this.get<HistoryRow[]>(DF_ENDPOINTS.history);
    }

    public async saveHistory(body: HistoryBody): Promise<HistoryRow | null> {
        if (!this.enabled) { return null; }
        return this.post<HistoryRow>(DF_ENDPOINTS.history, body);
    }

    public inventory(body: InventoryBody): Promise<InventoryPage> {
        return this.post(DF_ENDPOINTS.inventory, body, SEARCH_TIMEOUT);
    }

    public exportInventory(scope: InventoryScope): Promise<Blob> {
        return this.postBlob(DF_ENDPOINTS.inventoryExport, scope);
    }

    //*************************************************************************
    //  SharePoint PBL duplicate check (warnings only)
    //*************************************************************************
    /** Compares backlog items with the approved SharePoint PBL, or null when no API is configured. */
    public async checkPblDuplicates(items: PblCheckItem[]): Promise<PblCheckResponse | null> {
        if (!this.enabled) { return null; }
        return this.post<PblCheckResponse>(DF_ENDPOINTS.pblDuplicateCheck, { items }, PBL_CHECK_TIMEOUT);
    }

    //*************************************************************************
    //  PBL generator (drafts from the approved documents, for review)
    //*************************************************************************
    /** Last generation job, kept so the drafts survive leaving the PBLs step. */
    public lastPblJob: PblGenerateJob | null = null;

    /** Starts drafting PBLs from the approved documents of the last search run. */
    public startPblGeneration(documentIds: string[]): Promise<PblGenerateJob> {
        return this.post<PblGenerateJob>(DF_ENDPOINTS.pblGenerate, { runId: this.lastRunId, documentIds });
    }

    public getPblJob(jobId: string): Promise<PblGenerateJob> {
        return this.get<PblGenerateJob>(DF_ENDPOINTS.pblGenerateJob(jobId));
    }
}
